import { IAttachment } from 'src/app/shared/attachments/models/attachments.model';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, updateItem, patch, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { AttachmentService } from 'src/app/shared/attachments/service/attachments.service';
import { ApiResponse } from 'src/app/core/models/api-response';
import { GetUserEquivalentCompetences } from 'src/app/competence/state/competence.actions';
import { IEquivalentCompetencesModel } from 'src/app/competence/model/competence.model';
import { CompetenceUserApiService } from '../services/competence-user-api.service';
import {
  AddUserCompetence,
  ClearCurrentUserCompetence,
  DeleteUserCompetence,
  GetUserCompetenceById,
  LoadMyUserCompetences,
  LoadUserCompetences,
  RenewUserCompetence,
  UpdateUserCompetence,
} from './competence-user.actions';
import { CompetenceUserService } from '../services/competence-user.service';
import { ICompetenceUserModel, ICompetenceWithStatus } from '../models/competence-user.model';
import { OnSuccess } from '../../decorators/on-success.decorator';
import { FormDataService } from '../../attachments/service/form-data.service';

export interface CompetenceUserStateModel {
  competenceUsers: ICompetenceUserModel[] | undefined;
  competenceUserTotalPages: number;
  currentCompetenceUser: ICompetenceUserModel | undefined;
  userId: string;
  attachments: IAttachment[] | undefined;
  loaded: boolean;
  equivalentCompetences: ICompetenceWithStatus[];
}

const COMPETENCE_USER_STATE_TOKEN = new StateToken<CompetenceUserStateModel>('competenceUser');

@State<CompetenceUserStateModel>({
  name: COMPETENCE_USER_STATE_TOKEN,
  defaults: {
    loaded: false,
    competenceUsers: undefined,
    competenceUserTotalPages: 0,
    currentCompetenceUser: undefined,
    userId: '',
    attachments: undefined,
    equivalentCompetences: [],
  },
})
@Injectable()
export class CompetenceUserState {
  constructor(
    private serviceApi: CompetenceUserApiService,
    private service: CompetenceUserService
  ) {}

  @Action(LoadUserCompetences, { cancelUncompleted: true })
  loadUserCompetences(
    { getState, setState }: StateContext<CompetenceUserStateModel>,
    { userId, filter }: LoadUserCompetences
  ) {
    const userIdChecker = userId || getState().userId;
    return this.serviceApi.getUserCompetencesList(userIdChecker, filter).pipe(
      tap((response: ApiResponse<ICompetenceUserModel[]>) => {
        const state = getState();
        setState({
          ...state,
          competenceUsers: response.data,
          competenceUserTotalPages: response.totalPages,
          userId: userIdChecker,
        });
      })
    );
  }

  @Action(LoadMyUserCompetences, { cancelUncompleted: true })
  loadMyUserCompetences(
    { getState, setState }: StateContext<CompetenceUserStateModel>,
    { filter }: LoadMyUserCompetences
  ) {
    const userIdChecker = getState().userId;
    return this.serviceApi.getMyUserCompetencesList(filter).pipe(
      tap((response: ApiResponse<ICompetenceUserModel[]>) => {
        const state = getState();
        setState({
          ...state,
          competenceUsers: response.data,
          competenceUserTotalPages: response.totalPages,
          userId: userIdChecker,
        });
      })
    );
  }

  @Action(AddUserCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'userCompetenceAdded' })
  addCompetenceUser(
    ctx: StateContext<CompetenceUserStateModel>,
    { toAdd, formResponse }: AddUserCompetence
  ) {
    const form = FormDataService.mapToFormData({
      ...toAdd,
      formVersionId: formResponse?.formVersionId,
      response: formResponse?.response,
    });
    return this.serviceApi.addUserCompetence(form).pipe(
      tap(() => {
        this.service.routeBackToUserCompetencesList(toAdd.id);
      })
    );
  }

  @Action(RenewUserCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'userCompetenceRenewed' })
  renewCompetenceUser(
    ctx: StateContext<CompetenceUserStateModel>,
    { toRenew, formResponse }: RenewUserCompetence
  ) {
    const form = FormDataService.mapToFormData({
      ...toRenew,
      formVersionId: formResponse?.formVersionId,
      response: formResponse?.response,
    });
    return this.serviceApi.renewUserCompetence(form).pipe(
      tap(() => {
        this.service.routeBackToUserCompetencesList(toRenew.id);
      })
    );
  }

  @Action(GetUserCompetenceById)
  getCompetenceUserById(
    ctx: StateContext<CompetenceUserStateModel>,
    { userId, competenceId }: GetUserCompetenceById
  ) {
    // getting every existing competence user from state
    const competenceGroupsFromState = ctx.getState().competenceUsers;
    // if a competence user exist then just get updated (latest) from api
    const exist = competenceGroupsFromState?.find(
      (cu: ICompetenceUserModel) => cu.id === userId && cu.competenceId === competenceId
    );
    // if it does not exist then fetch it from the api
    return this.serviceApi.getUserCompetenceById(userId, competenceId).pipe(
      tap((response: ICompetenceUserModel | null) => {
        if (!response) {
          return;
        }

        response.attachments = AttachmentService.mapAttachments(response.attachments);

        ctx.setState(
          patch({
            competenceUsers: exist
              ? updateItem<ICompetenceUserModel>(
                  (toGet: ICompetenceUserModel | undefined) =>
                    toGet?.id === response.id && toGet?.competenceId === competenceId,
                  response
                )
              : insertItem<ICompetenceUserModel>(response),
            attachments: response.attachments,
            currentCompetenceUser: response,
          })
        );
      })
    );
  }

  @Action(GetUserEquivalentCompetences, { cancelUncompleted: true })
  getUserEquivalentCompetences(
    ctx: StateContext<CompetenceUserStateModel>,
    { userId, competenceId }: GetUserEquivalentCompetences
  ) {
    return this.serviceApi.getUserEquivalentCompetences(userId, competenceId).pipe(
      tap((response: IEquivalentCompetencesModel) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          equivalentCompetences: response.equivalentCompetences ?? [],
        });
      })
    );
  }

  throwError$ = of(null).pipe(throwError);

  @Action(DeleteUserCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'userCompetenceDeleted' })
  deleteCompetenceUser(
    ctx: StateContext<CompetenceUserStateModel>,
    { userId, competenceId }: DeleteUserCompetence
  ) {
    return this.serviceApi.deleteUserCompetence(userId, competenceId).pipe(
      tap(() => {
        ctx.setState(
          patch({
            competenceUsers: removeItem<ICompetenceUserModel>(
              (competenceUser: ICompetenceUserModel | undefined) =>
                competenceUser?.competenceId === competenceId
            ),
          })
        );
        this.service.routeBackToUserCompetencesList(userId);
      })
    );
  }

  @Action(UpdateUserCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'userCompetenceUpdated' })
  updateUserCompetence(
    { getState, setState }: StateContext<CompetenceUserStateModel>,
    { toUpdate, userId, competenceId }: UpdateUserCompetence
  ) {
    const form = FormDataService.mapToFormData(toUpdate);
    return this.serviceApi.updateUserCompetence(form, userId, competenceId).pipe(
      tap((response: IAttachment[]) => {
        const state = getState();
        response = AttachmentService.mapAttachments(response);
        setState({
          ...state,
          attachments: response,
        });
        this.service.routeBackToUserCompetencesList(userId);
      })
    );
  }

  @Action(ClearCurrentUserCompetence)
  clearCurrentUserCompetence(ctx: StateContext<CompetenceUserStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentCompetenceUser: undefined,
    });
  }

  @Selector()
  static userCompetencesLoaded(state: CompetenceUserStateModel) {
    return state.loaded;
  }

  @Selector()
  static getCompetenceUserTotalPages(state: CompetenceUserStateModel) {
    return state.competenceUserTotalPages;
  }

  @Selector()
  static getUserCompetencesAttachments(state: CompetenceUserStateModel) {
    return state.attachments;
  }

  @Selector()
  static getUserAllCompetences(state: CompetenceUserStateModel) {
    return state.competenceUsers;
  }

  @Selector()
  static getUserCompetenceByIdAndCompetenceId(state: CompetenceUserStateModel) {
    return (id: string, competenceId: string) => {
      return state.competenceUsers?.find(
        (competenceUser: ICompetenceUserModel) =>
          competenceUser.id === id && competenceUser.competenceId === competenceId
      );
    };
  }

  @Selector()
  static getUserEquivalentCompetences(state: CompetenceUserStateModel): ICompetenceWithStatus[] {
    return state.equivalentCompetences;
  }

  @Selector()
  static getCurrentCompetenceUser(state: CompetenceUserStateModel) {
    return state.currentCompetenceUser;
  }
}
